import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { filterData } from "../../../../utils/kendo";
import "./DropdownRenderer.scss";
// Import renderer components
import { MultiSelectCheckboxes, MultiSelectDropdown, OptionButtons, RegularDropdown } from "./DropdownRenderer/index";

const DropdownRenderer = ({ element, onChange, showResponses = false, isGridElement }) => {
  const { properties = {}, value } = element;
  const { assigneeList = [] } = useSelector((state) => state.tasks.defaultDetails);
  const [hasChoosen, setHasChoosen] = useState(false);
  const {
    label = "Select an option",
    options = [
      { label: "Option 1", value: "Option 1" },
      { label: "Option 2", value: "Option 2" },
      { label: "Option 3", value: "Option 3" },
    ],
    required = false,
    optionsView = false,
    showChipName: showChipNameRaw,
    multiSelect = false,
    isActiveMember = false,
  } = properties;
  const showChipName = showChipNameRaw === false ? false : true;
  const defaultValue = properties["default"] ? properties["default"] : multiSelect ? [] : null;

  /**
   * Prepare options for dropdown
   * @returns {Array} Prepared options
   * @author Sarthak Arora
   */
  const getPreparedOptions = () => {
    let filteredAssigneeList = assigneeList.filter((assignee) => !assignee?.isSubHeading && assignee?.label);

    let decidedArr = isActiveMember ? (filteredAssigneeList.length > 0 ? filteredAssigneeList : options) : options;
    const optionsWithGroup = decidedArr.map((option) => ({ ...option, group: "Available Options" }));
    if (options.length <= 12) {
      return optionsWithGroup;
    }
    return [...optionsWithGroup].sort((a, b) => {
      const labelA = a.label?.toString().toLowerCase() || "";
      const labelB = b.label?.toString().toLowerCase() || "";
      return labelA.localeCompare(labelB, undefined, { numeric: true, sensitivity: "base" });
    });
  };

  const [ddValue, setDdValue] = useState(
    multiSelect && !optionsView
      ? Array.isArray(value)
        ? value.map((item) => ({ ...item, group: "Selected Options" }))
        : []
      : Array.isArray(defaultValue)
      ? defaultValue.map((item) => ({ ...item, group: "Selected Options" }))
      : []
  );
  const [filteredOptions, setFilteredOptions] = useState(getPreparedOptions().map((option) => ({ ...option, group: "Available Options" })));

  // Check if field is empty and required (show red border immediately if no value and no default)
  const hasValue = value && value !== "" && value !== null && value !== undefined;
  const hasDefaultValue =
    defaultValue && defaultValue.value && defaultValue.value !== "" && defaultValue.value !== null && defaultValue.value !== undefined;
  const showValidationError = required && !hasValue && !hasDefaultValue && !showResponses;
  // Initialize selectedValue based on mode: use value prop in response mode, defaultValue in filling mode
  const [selectedValue, setSelectedValue] = useState(
    multiSelect
      ? value
        ? Array.isArray(value)
          ? value
          : []
        : defaultValue
        ? Array.isArray(defaultValue)
          ? defaultValue
          : []
        : []
      : value && typeof value === "object"
      ? value?.value
      : value
  );

  /**
   * Update filtered options when options prop changes
   * Sort alphanumerically if more than 12 options
   * @author Bhavana
   */
  useEffect(() => {
    setFilteredOptions(getPreparedOptions());
  }, [options]);

  /**
   * Handles the filter change event for the dropdown
   * @author Bhavana
   */
  const handleFilterChange = (event) => {
    const filteredList = filterData(event.filter, getPreparedOptions());
    setFilteredOptions([...filteredList]);
  };

  const resetFilter = () => {
    setFilteredOptions(getPreparedOptions());
  };

  /**
   * Handle option button click
   */
  const handleOptionClick = (optionValue) => {
    if (showResponses) return;
    if (multiSelect) {
      const currentValue = Array.isArray(selectedValue) ? selectedValue : [];
      const isSelected = currentValue.includes(optionValue);
      let newValue;
      if (isSelected) {
        newValue = currentValue.filter((val) => val !== optionValue);
      } else {
        newValue = [...currentValue, optionValue];
      }
      setSelectedValue(newValue);
      onChange && onChange(newValue);
    } else {
      setSelectedValue(optionValue);
      onChange && onChange(optionValue);
    }
  };

  // Check if options count is within limit for options view
  const validOptionsCount = options.filter((option) => option.label && option.label.trim()).length;

  return (
    <div className={`dropdown-renderer ${isGridElement ? "mb-0" : "mb-3"}`}>
      {showChipName && (
        <label htmlFor={`${element?.id}-dropdown`} className='form-label'>
          {element?.name}
        </label>
      )}
      {(() => {
        const getRenderKey = () => {
          if (multiSelect) {
            return validOptionsCount <= 12 ? (optionsView ? "ms-checkboxes" : "ms-dropdown") : "ms-dropdown";
          } else {
            return optionsView ? "ob-radio" : "ob-dropdown";
          }
        };
        const renderKey = getRenderKey();

        switch (renderKey) {
          case "ms-checkboxes":
            return (
              <MultiSelectCheckboxes
                options={filteredOptions}
                selectedValue={selectedValue}
                onOptionClick={handleOptionClick}
                required={required}
                showResponses={showResponses}
              />
            );

          case "ms-dropdown":
            return (
              <MultiSelectDropdown
                element={element}
                ddValue={ddValue}
                filteredOptions={filteredOptions}
                showResponses={showResponses}
                selectedValue={selectedValue}
                defaultValue={defaultValue}
                showValidationError={showValidationError}
                hasChoosen={hasChoosen}
                required={required}
                handleFilterChange={handleFilterChange}
                resetFilter={resetFilter}
                onChange={onChange}
                setDdValue={setDdValue}
                setHasChoosen={setHasChoosen}
              />
            );

          case "ob-radio":
            return (
              <OptionButtons
                element={element}
                filteredOptions={filteredOptions}
                showResponses={showResponses}
                showValidationError={showValidationError}
                onChange={onChange}
                selectedValue={selectedValue}
                onOptionClick={handleOptionClick}
              />
            );

          case "ob-dropdown":
          default:
            return (
              <RegularDropdown
                element={element}
                filteredOptions={filteredOptions}
                showResponses={showResponses}
                showValidationError={showValidationError}
                hasChoosen={hasChoosen}
                handleFilterChange={handleFilterChange}
                resetFilter={resetFilter}
                onChange={onChange}
                setHasChoosen={setHasChoosen}
              />
            );
        }
      })()}
    </div>
  );
};
export default DropdownRenderer;
